import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartValueAxis,
  ChartValueAxisItem,
} from "@progress/kendo-react-charts";

import {
  ChartCard,
  ChartToggle,
  CHART_COLORS,
  createStackedBarVisual,
  GRANULARITY_OPTIONS,
} from "../charts";
import { useRequestStatusOverview } from "../../hooks";
import { getCurrentCalendarPeriod } from "../../utils/dateRangeHelper";
import "./RequestStatusOverviewChart.scss";

/*
 * Request Status Overview (graph3).
 *
 * Each row is its own single-category Kendo bar chart rather than one chart
 * with two categories. That keeps the "name left, total right" header aligned
 * with its bar without measuring the plot area, and lets each total carry its
 * own colour - which a shared category axis cannot do.
 *
 * Both rows are pinned to the same value axis min/max so their segments line
 * up; only the lower chart draws its axis, which becomes the shared tick row.
 */

const ROW_CHART_STYLE = { height: "52px", width: "100%" };
const AXIS_CHART_STYLE = { height: "76px", width: "100%" };
const CHART_AREA = { margin: { top: 0, right: 0, bottom: 0, left: 0 } };

const HIDDEN_GRID = { visible: false };
const HIDDEN_LINE = { visible: false };

const SEGMENT_LABELS = {
  visible: true,
  background: "none",
  color: "#ffffff",
  font: "600 13px Poppins, sans-serif",
  content: (e) => (e.value > 0 ? e.value : ""),
  position: "center",
  align: "center",
};

const TOOLTIP_CONFIG = {
  visible: true,
  background: "#5f91d1",
  color: "#ffffff",
  font: "12px Poppins, sans-serif",
  border: { width: 0 },
  padding: { top: 8, right: 12, bottom: 8, left: 12 },
};

const StatusRow = ({ group, axisMax, showAxis, axisTicks, selectedYear }) => {
  /*
   * A trailing "remainder" series pads the stack out to the axis max, which is
   * the grey track behind the Rejected row. It carries no label and stays out
   * of the legend.
   */
  const remainder = Math.max(0, axisMax - group.total);

  const seriesData = useMemo(
    () => group.segments.filter((segment) => segment.value > 0),
    [group.segments],
  );

  const lastIndex = seriesData.length - 1;

  return (
    <div className="status-overview__row">
      <div className="status-overview__row-head">
        <span className="status-overview__row-label">{group.groupLabel}</span>
        <span className="status-overview__row-total" style={{ color: group.totalColor }}>
          {group.total}
        </span>
      </div>

      <Chart
        style={showAxis ? AXIS_CHART_STYLE : ROW_CHART_STYLE}
        chartArea={CHART_AREA}
        renderAs="svg"
        transitions={false}
        seriesDefaults={{ type: "bar", stack: true }}
      >
        <ChartLegend visible={false} />

        <ChartCategoryAxis>
          <ChartCategoryAxisItem
            categories={[group.groupLabel]}
            visible={false}
            majorGridLines={HIDDEN_GRID}
            line={HIDDEN_LINE}
          />
        </ChartCategoryAxis>

        <ChartValueAxis>
          <ChartValueAxisItem
            min={0}
            max={axisMax}
            visible={showAxis}
            majorGridLines={HIDDEN_GRID}
            line={HIDDEN_LINE}
            labels={
              showAxis
                ? {
                    font: "12px Poppins, sans-serif",
                    color: CHART_COLORS.axisTick,
                    margin: { top: 10 },
                    content: (e) => (axisTicks.includes(e.value) ? `${e.value}` : ""),
                  }
                : { visible: false }
            }
          />
        </ChartValueAxis>

        {/*
         * One flat array rather than a mapped list plus a conditional: Kendo's
         * ChartSeries validator iterates children and reads child.type, which
         * a nested array or a null child both trip.
         */}
        <ChartSeries>
          {[
            ...seriesData.map((segment, index) => (
              <ChartSeriesItem
                key={segment.key}
                name={segment.label}
                data={[segment.value]}
                color={segment.color}
                labels={SEGMENT_LABELS}
                tooltip={{
                  ...TOOLTIP_CONFIG,
                  render: ({ dataItem }) => (
                    <div style={{ lineHeight: "1.6" }}>
                      <div>{selectedYear?.text || "Period"}</div>
                      <div>{segment.label}: {segment.value}</div>
                    </div>
                  ),
                }}
                visual={createStackedBarVisual({
                  roundStart: index === 0,
                  // Rounded on the right only when nothing follows it in the stack.
                  roundEnd: index === lastIndex && remainder === 0,
                })}
              />
            )),
            remainder > 0 && (
              <ChartSeriesItem
                key="remainder"
                name="remainder"
                data={[remainder]}
                color={CHART_COLORS.statusTrack}
                visibleInLegend={false}
                visual={createStackedBarVisual({ roundStart: !seriesData.length, roundEnd: true })}
              />
            ),
          ].filter(Boolean)}
        </ChartSeries>
      </Chart>
    </div>
  );
};

const RequestStatusOverviewChart = ({ providerId, scope }) => {
  const [selectedYear, setSelectedYear] = useState(null);

  const { granularity, setGranularity, groups, axisMax, axisTicks, legend, isEmpty, isLoading, calendarPeriods } =
    useRequestStatusOverview(providerId, selectedYear?.text, scope);

  const yearOptions = useMemo(() => {
    return calendarPeriods.map((item) => {
      return {
        text: item.period_name,
        value: item.period_mstr_key,
      };
    });
  }, [calendarPeriods]);

  useEffect(() => {
    if (yearOptions.length > 0) {
      const currentPeriod = getCurrentCalendarPeriod(calendarPeriods, granularity);
      setSelectedYear(yearOptions.find((option) => option.value === currentPeriod?.period_mstr_key) || yearOptions[0]);
    }
  }, [calendarPeriods, granularity, yearOptions]);

  const handleYearChange = useCallback(
    (value) => {
      // value is the selected item object from the dropdown: { text, value }
      if (value && value.text) {
        setSelectedYear(value);
      }
    },
    []
  );

  const toggles = (
    <ChartToggle
      options={GRANULARITY_OPTIONS}
      value={granularity}
      onChange={setGranularity}
      ariaLabel="Status period"
    />
  );

  const currentPeriodLabel = GRANULARITY_OPTIONS.find(opt => opt.value === granularity)?.label || "Period";

  return (
    <ChartCard
      title="Request Status Overview"
      toggles={toggles}
      yearOptions={yearOptions}
      selectedYear={selectedYear}
      onYearChange={handleYearChange}
      periodTypeLabel={currentPeriodLabel}
      isLoading={isLoading}
      isEmpty={isEmpty}
    >
      <div className="status-overview">
        {groups.map((group, index) => (
          <StatusRow
            key={group.groupKey}
            group={group}
            axisMax={axisMax}
            axisTicks={axisTicks}
            showAxis={index === groups.length - 1}
            selectedYear={selectedYear}
          />
        ))}

        <div className="chart-legend-set status-overview__legend">
          {legend.map((item) => (
            <span key={item.key} className="chart-legend-item status-overview__legend-item">
              <i
                className="status-overview__legend-marker"
                style={{ backgroundColor: item.color }}
              />
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </ChartCard>
  );
};

export default memo(RequestStatusOverviewChart);
